import { BrandNames } from 'constants/brandNames';
import { PageNames } from 'constants/pageNames';

const THEME_SUFFIX = '-theme';

/**
 * Returns the default brand theme.
 *
 * @returns {BrandNames} The default brand name, which is set to `BrandNames.Privatsikring`.
 */
export const getDefaultTheme = (): BrandNames => BrandNames.Privatsikring;

/**
 * Resolves the brand name from a given string, ignoring case sensitivity.
 * If the brand is not recognized, it falls back to the default theme.
 *
 * @param theme - The brand name to resolve.
 * @returns The matching `BrandNames` enum value, or the default theme if not found.
 */
export const resolveBrandName = (theme?: string | null): BrandNames => {
	theme = normalizeThemeInput(theme);
	return themeMapping.get(theme) ?? getDefaultTheme();
};

/**
 * Constructs the theme class name (e.g., "Codan-theme") from a given brand name.
 * Automatically appends the theme suffix and falls back to the default theme if not recognized.
 *
 * @param theme - The brand name to resolve and format.
 * @returns The formatted theme class name, such as "Codan-theme" or "Almbrand-theme".
 */
export const resolveThemeClass = (theme?: string | null): ThemeClassName => {
	const brand = resolveBrandName(theme);
	return `${brand}${THEME_SUFFIX}` as ThemeClassName;
};

/**
 * Maps a theme/brand name to its corresponding external homepage URL.
 * Used for redirecting users to the appropriate brand website.
 *
 * @param theme - The brand name to resolve
 * @returns The URL for the brand's open pages
 */
export const getOpenPagesUrlForBrand = (theme?: string | null): string => {
	theme = normalizeThemeInput(theme);
	const brand = themeMapping.get(theme ?? '');

	const urlMap: Record<BrandNames, string> = {
		[BrandNames.Codan]: PageNames.CodanOpenPages,
		[BrandNames.Privatsikring]: PageNames.PrivatsikringOpenPages,
		[BrandNames.Almbrand]: PageNames.AlmbrandOpenPages,
	};

	return urlMap[brand as BrandNames] || urlMap[getDefaultTheme()];
};

/**
 * Normalizes the theme input by removing the theme suffix if present.
 * This handles cases where the API returns themes with the suffix already appended.
 *
 * @param theme - The theme string that may or may not include the suffix
 * @returns The normalized theme name without the suffix
 */
const normalizeThemeInput = (theme?: string | null): string => {
	if (!theme) return '';

	const lowerCaseTheme = theme.toLowerCase();
	if (lowerCaseTheme.endsWith(THEME_SUFFIX)) {
		return lowerCaseTheme.slice(0, -THEME_SUFFIX.length);
	}

	return lowerCaseTheme;
};

/**
 * Generates a case-insensitive mapping of brand names to their enum values.
 * This mapping is used to quickly resolve a theme name to its corresponding BrandName.
 */
const themeMapping = Object.values(BrandNames).reduce((acc, brand) => {
	acc.set(brand.toLowerCase(), brand);
	return acc;
}, new Map<string, BrandNames>());
